import type { Node, Edge } from '@xyflow/react'
import type { FlowNode, FlowEdge } from '@/schemas/workflow-node'

/**
 * Pure helper extracted from `workflow-store.ts` `setFlowFromWorkflow`.
 *
 * Normalizes an arbitrary workflow node/edge payload into the exact
 * React Flow `Node<FlowNode>[]` / `Edge[]` shape consumed by
 * `<ReactFlow>`. Reused by:
 *   - `workflow-store.setFlowFromWorkflow` (editor canvas hydration)
 *   - `diff-canvas.tsx` (read-only diff overlay)
 *
 * Behavior contract (MUST match the pre-Phase-03 editor behavior):
 *   - Node position may arrive as `{x, y}`, a scalar index, or be
 *     missing entirely. Missing/invalid → lay out vertically at
 *     `(250, 80 + idx * 140)`.
 *   - Edge `condition` may arrive as a plain string branch label
 *     (seeded workflows). When not a `{ operator, left, ... }` object,
 *     treat it as a branch label and null out the condition — otherwise
 *     the backend `ConditionExpression` validation 422s on persist.
 */
export function normalizeGraphToReactFlow(
  nodes: ReadonlyArray<Record<string, unknown>> | undefined | null,
  edges: ReadonlyArray<Record<string, unknown>> | undefined | null
): { nodes: Node<FlowNode>[]; edges: Edge[] } {
  const flowNodes: Node<FlowNode>[] = (nodes ?? []).map((node, index) => {
    const raw = node.position
    let position: { x: number; y: number }
    if (
      raw &&
      typeof raw === 'object' &&
      typeof (raw as { x?: unknown }).x === 'number' &&
      typeof (raw as { y?: unknown }).y === 'number'
    ) {
      position = raw as { x: number; y: number }
    } else {
      const idx = typeof raw === 'number' ? raw : index
      position = { x: 250, y: 80 + idx * 140 }
    }
    const nodeType = (node.type as string) ?? 'llm'
    const data = {
      ...(node as object),
      type: nodeType,
      id: (node.id as string) ?? '',
      label: (node.label as string) ?? '',
      position,
      config:
        node.config && typeof node.config === 'object' ? node.config : {},
      input_schema: Array.isArray(node.input_schema) ? node.input_schema : [],
      output_schema: Array.isArray(node.output_schema)
        ? node.output_schema
        : [],
      variable_mappings: Array.isArray(node.variable_mappings)
        ? node.variable_mappings
        : [],
      error_handling:
        node.error_handling &&
        typeof node.error_handling === 'object' &&
        'retry_count' in (node.error_handling as object)
          ? node.error_handling
          : {
              retry_count: 0,
              retry_backoff: 'linear',
              fallback_node_id: null,
              on_error: 'fail_workflow',
              default_output: null,
            },
    }
    return {
      id: (node.id as string) ?? '',
      type: nodeType,
      position,
      data: data as unknown as FlowNode,
    }
  })

  const flowEdges: Edge[] = (edges ?? []).map((edge) => {
    const rawCond = edge.condition
    const isValidCondition =
      rawCond !== null &&
      typeof rawCond === 'object' &&
      'operator' in (rawCond as object)
    const condition = isValidCondition ? rawCond : null
    const branchLabel =
      typeof rawCond === 'string'
        ? rawCond
        : ((edge.branch_label as string | null) ?? null)
    return {
      id: (edge.id as string) ?? `${edge.source}-${edge.target}`,
      source: (edge.source as string) ?? '',
      target: (edge.target as string) ?? '',
      data: {
        ...(edge as object),
        condition,
        branch_label: branchLabel,
      } as unknown as FlowEdge,
    }
  })

  return { nodes: flowNodes, edges: flowEdges }
}
